import React from 'react';
import { CheckCircle, AlertCircle, DollarSign, Clock, Shield } from 'lucide-react';

interface ThreeCardsProps {
  plan: {
    status: 'planned' | 'ready' | 'in-progress' | 'complete';
    summary: string;
    details: string;
    phaseCount: number;
  };
  cost: {
    estimated: number;
    ceiling: number;
    status: 'under' | 'at' | 'over';
    currency: string;
  };
  risk: {
    level: 'low' | 'medium' | 'high';
    issues: string[];
    mitigations: string[];
  };
  onViewDetails?: (card: 'plan' | 'cost' | 'risk') => void;
}

const statusColors = {
  low: 'var(--color-accent)',
  medium: 'var(--color-secondary)',
  high: 'var(--color-destructive)',
  under: 'var(--color-accent)',
  at: 'var(--color-secondary)',
  over: 'var(--color-destructive)',
  planned: 'var(--color-muted)',
  ready: 'var(--color-accent)',
  'in-progress': 'var(--color-secondary)',
  complete: 'var(--color-accent)',
} as const;

const statusIcons = {
  plan: Clock,
  cost: DollarSign,
  risk: Shield,
} as const;

export function ThreeCards({ plan, cost, risk, onViewDetails }: ThreeCardsProps) {
  const cards = [
    {
      id: 'plan',
      title: 'Plan',
      icon: statusIcons.plan,
      status: plan.status,
      primary: `${plan.phaseCount} phases`,
      secondary: plan.summary,
      details: plan.details,
      color: statusColors[plan.status],
    },
    {
      id: 'cost',
      title: 'Cost',
      icon: statusIcons.cost,
      status: cost.status,
      primary: `${cost.currency}${cost.estimated.toFixed(2)}`,
      secondary: `Ceiling: ${cost.currency}${cost.ceiling.toFixed(2)}`,
      details: `${((cost.estimated / cost.ceiling) * 100).toFixed(1)}% of budget`,
      color: statusColors[cost.status],
    },
    {
      id: 'risk',
      title: 'Risk',
      icon: statusIcons.risk,
      status: risk.level,
      primary: risk.level.toUpperCase(),
      secondary: `${risk.issues.length} issues`,
      details: risk.issues.slice(0, 2).join('; ') + (risk.issues.length > 2 ? '...' : ''),
      color: statusColors[risk.level],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {cards.map((card) => {
        const Icon = card.icon;
        const statusIcon = card.status === 'low' || card.status === 'under' || card.status === 'ready' || card.status === 'complete' 
          ? CheckCircle 
          : AlertCircle;

        return (
          <div
            key={card.id}
            className={`
              group relative bg-background rounded-lg border border-border 
              p-6 shadow-sm hover:shadow-md transition-all duration-200
              hover:border-[color:var(--color-primary)] cursor-pointer
              min-h-[200px] flex flex-col
            `}
            style={{ 
              '--card-color': card.color 
            } as React.CSSProperties}
            onClick={() => onViewDetails?.(card.id as any)}
            role="button"
            tabIndex={0}
            aria-label={`View ${card.title} details`}
          >
            {/* Header with icon */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div 
                  className="p-2 rounded-md bg-[color:var(--color-primary)/0.1] group-hover:bg-[color:var(--color-primary)/0.2]"
                  style={{ 
                    borderColor: `hsl(${card.color})`,
                    backgroundColor: `hsl(${card.color} / 0.1)`
                  }}
                >
                  <Icon className="w-5 h-5 text-[color:var(--color-primary)]" />
                </div>
                <h3 className="text-lg font-semibold text-text">
                  {card.title}
                </h3>
              </div>
              
              <statusIcon 
                className={`
                  w-5 h-5 transition-colors
                  ${card.status === 'low' || card.status === 'under' || card.status === 'ready' || card.status === 'complete'
                    ? 'text-success'
                    : 'text-error'}
                `}
              />
            </div>

            {/* Status badge */}
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4
              bg-[color:var(--card-color)/0.1] text-[color:var(--card-color)]
              border border-[color:var(--card-color)/0.2]">
              {card.status.toUpperCase()}
            </div>

            {/* Primary metric */}
            <div className="text-2xl font-bold text-text mb-2">
              {card.primary}
            </div>

            {/* Secondary info */}
            <p className="text-text-secondary text-sm mb-4 flex-1">
              {card.secondary}
            </p>

            {/* Details preview */}
            {card.details && (
              <p className="text-text-secondary text-xs leading-relaxed">
                {card.details}
              </p>
            )}

            {/* Hover overlay for interactivity */}
            <div className={`
              absolute inset-0 bg-gradient-to-t from-[color:var(--color-primary)/0] 
              via-[color:var(--color-primary)/0] to-[color:var(--color-primary)/0.02]
              opacity-0 group-hover:opacity-100 transition-opacity duration-200
              pointer-events-none rounded-lg
            `} />

            {/* Focus ring for accessibility */}
            <div className="
              absolute inset-0 rounded-lg ring-2 ring-transparent 
              focus-within:ring-[color:var(--color-primary)] focus-within:ring-offset-2
              focus-within:ring-offset-background
              pointer-events-none
            " />
          </div>
        );
      })}
    </div>
  );
}
